import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShield, FiMail, FiPhone, FiMapPin } = FiIcons;

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-8"
        >
          <div className="flex items-center mb-8">
            <SafeIcon icon={FiShield} className="text-red-500 text-3xl mr-4" />
            <h1 className="text-4xl font-bold text-gray-900">Datenschutzerklärung</h1>
          </div>

          <div className="prose prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Datenschutz auf einen Blick</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Allgemeine Hinweise</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Die folgenden Hinweise geben einen einfachen Überblick darüber, was mit Ihren personenbezogenen Daten passiert, wenn Sie diese Website besuchen. Personenbezogene Daten sind alle Daten, mit denen Sie persönlich identifiziert werden können.
              </p>

              <div className="bg-blue-50 p-6 rounded-lg border-l-4 border-blue-500 mb-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Wichtiger Hinweis</h3>
                <p className="text-blue-800 leading-relaxed">
                  Diese Website verwendet keine Cookies und kein Tracking. Wir verzichten bewusst auf Analysedienste wie Google Analytics oder ähnliche Tools.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Datenerfassung auf dieser Website</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Wer ist verantwortlich für die Datenerfassung auf dieser Website?</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Die Datenverarbeitung auf dieser Website erfolgt durch den Websitebetreiber. Dessen Kontaktdaten können Sie dem Impressum dieser Website entnehmen.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 mb-3">Wie erfassen wir Ihre Daten?</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Ihre Daten werden zum einen dadurch erhoben, dass Sie uns diese mitteilen. Hierbei kann es sich z. B. um Daten handeln, die Sie in ein Kontaktformular eingeben.
              </p>

              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Erhobene Daten im Kontaktformular:</h4>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Vor- und Nachname</li>
                  <li>E-Mail-Adresse</li>
                  <li>Telefonnummer (optional)</li>
                  <li>Alter (optional)</li>
                  <li>Beruf (optional)</li>
                  <li>Nachricht</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Verwendete Services</h2>
              
              <div className="space-y-6">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Boost.space</h3>
                  <p className="text-gray-700">
                    Als Datenschnittstelle zur Automatisierung von Prozessen. Ihre Daten werden dabei gemäß DSGVO-Standards verarbeitet.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Nextcloud</h3>
                  <p className="text-gray-700">
                    Für die sichere Speicherung und Verwaltung von Terminen, Dokumenten und Kontaktdaten nutzen wir Nextcloud. Die Daten werden ausschließlich auf Servern innerhalb der Europäischen Union verarbeitet. Nextcloud wird von uns selbst oder von einem europäischen Anbieter DSGVO-konform betrieben.
                  </p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Acumbamail</h3>
                  <p className="text-gray-700">
                    Für den E-Mail-Versand nutzen wir Acumbamail. Ihre E-Mail-Adresse wird nur für die Kommunikation bezüglich Ihrer Coaching-Anfrage verwendet.
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Rechtsgrundlage der Verarbeitung</h2>
              <p className="text-gray-700 leading-relaxed">
                Die Verarbeitung Ihrer personenbezogenen Daten erfolgt auf Grundlage Ihrer Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO. Die Datenverarbeitung dient der Bearbeitung Ihrer Anfrage und der Kommunikation mit Ihnen.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Zweck der Datenverarbeitung</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Bearbeitung Ihrer Coaching-Anfrage</li>
                <li>Terminvereinbarung und -koordination</li>
                <li>Kommunikation bezüglich Ihrer Anfrage</li>
                <li>Bereitstellung von Informationen (nur bei Einwilligung)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Speicherdauer</h2>
              <p className="text-gray-700 leading-relaxed">
                Ihre Daten werden nur so lange gespeichert, wie es für die Bearbeitung Ihrer Anfrage notwendig ist. Nach Abschluss der Kommunikation werden die Daten gelöscht, es sei denn, gesetzliche Aufbewahrungsfristen erfordern eine längere Speicherung.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Ihre Rechte</h2>
              <p className="text-gray-700 leading-relaxed mb-4">Sie haben jederzeit das Recht:</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Auskunft über Ihre bei uns gespeicherten personenbezogenen Daten zu erhalten</li>
                <li>Berichtigung unrichtiger personenbezogener Daten zu verlangen</li>
                <li>Löschung Ihrer bei uns gespeicherten personenbezogenen Daten zu verlangen</li>
                <li>Einschränkung der Datenverarbeitung zu verlangen</li>
                <li>Widerspruch gegen die Verarbeitung Ihrer Daten zu erheben</li>
                <li>Datenübertragbarkeit zu verlangen</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Widerruf Ihrer Einwilligung</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Sie können Ihre Einwilligung zur Datenverarbeitung jederzeit per E-Mail an{' '}
                <a href="mailto:mike.trepte@biete-orientierung.de" className="text-red-500 hover:text-red-600 underline">
                  mike.trepte@biete-orientierung.de
                </a>{' '}
                widerrufen. Der Widerruf berührt nicht die Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Kontakt bei Datenschutzfragen</h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-4">Bei Fragen zum Datenschutz wenden Sie sich bitte an:</p>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <SafeIcon icon={FiMail} className="text-red-500 mr-2" />
                    <a href="mailto:mike.trepte@biete-orientierung.de" className="text-red-500 hover:text-red-600">
                      mike.trepte@biete-orientierung.de
                    </a>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiPhone} className="text-red-500 mr-2" />
                    <span className="text-gray-700">0561 / 43027276</span>
                  </div>
                  <div className="flex items-start">
                    <SafeIcon icon={FiMapPin} className="text-red-500 mr-2 mt-1" />
                    <div className="text-gray-700">
                      <p>Nadine Rau u. Mike Trepte GbR</p>
                      <p>Bettenhäuserstr. 1</p>
                      <p>34123 Kassel</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;